import React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { CancelButton, CloseButton, SaveChangesButton } from './StandardButtons';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

interface GenericConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** true: a dialog that only informs -- one button (the confirm one); the header's X still closes it */
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The in-app "are you sure?" dialog: same overlay / header / footer / buttons as every form (GenericForm's compact
 * size), portaled to document.body. Unlike window.confirm it is part of the page -- styled with the tokens, usable on
 * every browser and testable like any other UI. Backdrop click and Escape cancel.
 */
export function GenericConfirmDialog({ isOpen, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', hideCancel = false, onConfirm, onCancel }: GenericConfirmDialogProps) {
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  return createPortal(
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div role="alertdialog" aria-modal="true" aria-label={title} className="generic-form-container w-full max-w-md" style={{ maxHeight: 'var(--form-max-height-compact)' }}>
        <div className={cn('generic-form-header', 'generic-form-header-compact')} style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">{title}</h2>
          <CloseButton onClick={onCancel} />
        </div>
        <div className="generic-form-body generic-form-body-compact">
          <p className="text-sm text-gray-700">{message}</p>
        </div>
        <div className={cn('generic-form-footer', 'generic-form-footer-compact')} style={{ backgroundColor: colors.formHeaderFooter }}>
          {!hideCancel && <CancelButton onClick={onCancel}>{cancelLabel}</CancelButton>}
          <SaveChangesButton type="button" onClick={onConfirm}>{confirmLabel}</SaveChangesButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
